import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronRight,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

import PermissionGate from '../components/PermissionGate';
import { accessControlAPI } from '../services/api';

const categoryLabels = {
  dashboard: 'Dashboard',
  identity: 'Platform Identity',
  pii: 'PII Data',
  documents: 'Documents',
  consent: 'Consent Context',
  audit: 'Audit',
  ropa: 'ROPA',
  access_control: 'Access Control',
  retention: 'Retention / Revocation',
  settings: 'Settings',
  other: 'Other',
};

const riskStyles = {
  low: 'bg-slate-100 text-slate-700 ring-slate-200',
  medium: 'bg-blue-50 text-blue-700 ring-blue-100',
  high: 'bg-orange-50 text-orange-700 ring-orange-100',
  critical: 'bg-red-50 text-red-700 ring-red-100',
};

const tabLabels = {
  overview: 'Overview',
  members: 'Members',
  permissions: 'Permissions',
  policy: 'Policy',
};

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

function RiskBadge({ risk }) {
  return (
    <span
      className={cx(
        'rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ring-1',
        riskStyles[risk] || riskStyles.low,
      )}
    >
      {risk || 'low'}
    </span>
  );
}

function EmptyState({ title, description, action }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-600">
        <ShieldCheck className="h-6 w-6" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-slate-950">{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">{description}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

function Modal({ title, description, onClose, children, footer, width = 'max-w-2xl' }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-8 backdrop-blur-sm">
      <div className={cx('flex max-h-[92vh] w-full flex-col rounded-3xl bg-white shadow-2xl', width)}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <h2 className="text-lg font-bold text-slate-950">{title}</h2>
            {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="border-t border-slate-200 px-6 py-5">{footer}</div>}
      </div>
    </div>
  );
}

export default function GroupsPermissionsPage() {
  const [groups, setGroups] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');

  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [memberListSearch, setMemberListSearch] = useState('');

  const [permissionDraftCodes, setPermissionDraftCodes] = useState([]);
  const [permissionEditing, setPermissionEditing] = useState(false);
  const [showPermissionReview, setShowPermissionReview] = useState(false);

  const [showAddMembers, setShowAddMembers] = useState(false);
  const [memberCandidates, setMemberCandidates] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberCandidatesLoading, setMemberCandidatesLoading] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState({});
  const candidateRequestRef = useRef(0);

  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });

  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [error, setError] = useState('');

  const loadBaseData = async ({ preserveLoading = false } = {}) => {
    if (!preserveLoading) setLoading(true);
    setError('');

    try {
      const [groupsRes, permissionsRes] = await Promise.all([
        accessControlAPI.getGroups(),
        accessControlAPI.getPermissions(),
      ]);
      setGroups(groupsRes.data || []);
      setPermissions(permissionsRes.data || []);
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || 'Failed to load access control data.',
      );
    } finally {
      if (!preserveLoading) setLoading(false);
    }
  };

  const loadGroupDetail = async (groupId) => {
    if (!groupId) return;
    setDetailLoading(true);
    setError('');

    try {
      const res = await accessControlAPI.getGroupDetail(groupId);
      const detail = res.data;
      setSelectedGroup(detail);
      setPermissionDraftCodes((detail?.permissions || []).map((item) => item.code));
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load group details.');
    } finally {
      setDetailLoading(false);
    }
  };

  const openGroup = async (groupId, tab = 'overview') => {
    setSelectedGroupId(groupId);
    setSelectedGroup(null);
    setActiveTab(tab);
    setPermissionEditing(false);
    setShowPermissionReview(false);
    setMemberListSearch('');
    await loadGroupDetail(groupId);
  };

  const closeGroup = () => {
    setSelectedGroupId(null);
    setSelectedGroup(null);
    setActiveTab('overview');
    setPermissionEditing(false);
    setShowPermissionReview(false);
    setShowAddMembers(false);
    setSelectedCandidates({});
  };

  const loadMemberCandidates = async (groupId, search = '') => {
    if (!groupId) return;
    const requestId = candidateRequestRef.current + 1;
    candidateRequestRef.current = requestId;
    setMemberCandidatesLoading(true);

    try {
      const res = await accessControlAPI.getGroupMemberCandidates(groupId, search);
      if (candidateRequestRef.current !== requestId) return;
      setMemberCandidates(res.data?.results || []);
    } catch (err) {
      if (candidateRequestRef.current !== requestId) return;
      setError(err.response?.data?.detail || err.message || 'Failed to load available users.');
    } finally {
      if (candidateRequestRef.current === requestId) {
        setMemberCandidatesLoading(false);
      }
    }
  };

  useEffect(() => {
    loadBaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showAddMembers || !selectedGroupId) return undefined;
    const timer = window.setTimeout(() => {
      loadMemberCandidates(selectedGroupId, memberSearch);
    }, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddMembers, selectedGroupId, memberSearch]);

  const filteredGroups = useMemo(() => {
    const search = groupSearch.trim().toLowerCase();
    if (!search) return groups;
    return groups.filter((group) =>
      [group.name, group.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search)),
    );
  }, [groups, groupSearch]);

  const permissionByCode = useMemo(
    () => Object.fromEntries(permissions.map((permission) => [permission.code, permission])),
    [permissions],
  );

  const originalPermissionCodes = useMemo(
    () => (selectedGroup?.permissions || []).map((permission) => permission.code),
    [selectedGroup],
  );

  const permissionChanges = useMemo(() => {
    const before = new Set(originalPermissionCodes);
    const after = new Set(permissionDraftCodes);
    return {
      added: permissionDraftCodes.filter((code) => !before.has(code)),
      removed: originalPermissionCodes.filter((code) => !after.has(code)),
    };
  }, [originalPermissionCodes, permissionDraftCodes]);

  const hasPermissionChanges =
    permissionChanges.added.length > 0 || permissionChanges.removed.length > 0;

  const permissionsByCategory = useMemo(() => {
    const source = permissionEditing ? permissions : selectedGroup?.permissions || [];
    return source.reduce((acc, permission) => {
      const category = permission.category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(permission);
      return acc;
    }, {});
  }, [permissionEditing, permissions, selectedGroup]);

  const filteredMembers = useMemo(() => {
    const members = selectedGroup?.members || [];
    const search = memberListSearch.trim().toLowerCase();
    if (!search) return members;
    return members.filter((member) =>
      [member.full_name, member.email]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search)),
    );
  }, [selectedGroup, memberListSearch]);

  const totalMembers = useMemo(
    () => groups.reduce((total, group) => total + Number(group.members_count || 0), 0),
    [groups],
  );

  const togglePermission = (code) => {
    if (!permissionEditing) return;
    setPermissionDraftCodes((current) =>
      current.includes(code) ? current.filter((item) => item !== code) : [...current, code],
    );
  };

  const startPermissionEdit = () => {
    setPermissionDraftCodes(originalPermissionCodes);
    setPermissionEditing(true);
  };

  const cancelPermissionEdit = () => {
    setPermissionDraftCodes(originalPermissionCodes);
    setPermissionEditing(false);
    setShowPermissionReview(false);
  };

  const savePermissions = async () => {
    if (!selectedGroup || !hasPermissionChanges) return;
    setSavingPermissions(true);
    setError('');

    try {
      const res = await accessControlAPI.setGroupPermissions(
        selectedGroup.id,
        permissionDraftCodes,
      );
      setSelectedGroup(res.data);
      setPermissionDraftCodes((res.data?.permissions || []).map((item) => item.code));
      setPermissionEditing(false);
      setShowPermissionReview(false);
      await loadBaseData({ preserveLoading: true });
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to update permissions.');
    } finally {
      setSavingPermissions(false);
    }
  };

  const openAddMembers = () => {
    setMemberSearch('');
    setSelectedCandidates({});
    setMemberCandidates([]);
    setShowAddMembers(true);
  };

  const toggleCandidate = (candidate) => {
    if (!candidate.assignable) return;
    setSelectedCandidates((current) => {
      const next = { ...current };
      if (next[candidate.candidate_key]) delete next[candidate.candidate_key];
      else next[candidate.candidate_key] = candidate;
      return next;
    });
  };

  const addSelectedMembers = async () => {
    if (!selectedGroup) return;
    const candidates = Object.values(selectedCandidates).map((candidate) => ({
      source: candidate.source,
      id: candidate.id,
    }));
    if (!candidates.length) return;

    setAddingMembers(true);
    setError('');
    try {
      const res = await accessControlAPI.addGroupMemberCandidates(
        selectedGroup.id,
        candidates,
      );
      setSelectedGroup(res.data);
      setShowAddMembers(false);
      setSelectedCandidates({});
      await loadBaseData({ preserveLoading: true });
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          err.message ||
          'Failed to add members.',
      );
    } finally {
      setAddingMembers(false);
    }
  };

  const removeMember = async (userId) => {
    if (!selectedGroup) return;
    if (!window.confirm('Remove this user from the group? Their effective access may change immediately.')) {
      return;
    }

    setError('');
    try {
      await accessControlAPI.removeGroupMember(selectedGroup.id, userId);
      await Promise.all([
        loadGroupDetail(selectedGroup.id),
        loadBaseData({ preserveLoading: true }),
      ]);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to remove member.');
    }
  };

  const createCustomGroup = async () => {
    if (!newGroup.name.trim()) {
      setError('Group name is required.');
      return;
    }

    setCreatingGroup(true);
    setError('');
    try {
      const res = await accessControlAPI.createGroup({
        name: newGroup.name.trim(),
        description: newGroup.description.trim(),
        is_active: true,
        policy_json: {
          field_scopes: [],
          document_scopes: [],
          purpose_codes: [],
          default_view_mode: 'metadata_only',
          allow_full_view: false,
          requires_justification: true,
          session_ttl_minutes: 15,
        },
      });
      setShowCreateGroup(false);
      setNewGroup({ name: '', description: '' });
      await loadBaseData({ preserveLoading: true });
      await openGroup(res.data.id, 'overview');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create group.');
    } finally {
      setCreatingGroup(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="flex items-center gap-3 rounded-2xl border bg-white px-6 py-5 shadow-sm">
          <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
          <span className="text-sm font-medium text-slate-700">Loading access control...</span>
        </div>
      </div>
    );
  }

  return (
    <PermissionGate permission="manage_groups" showFallback>
      <div className="space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-orange-50 p-3 text-orange-600 ring-1 ring-orange-100">
              <KeyRound className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-950">Access Groups</h1>
              <p className="mt-1 text-sm text-slate-500">
                Review group access safely, then manage members or permissions in dedicated views.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setShowCreateGroup(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700"
            >
              <Plus className="h-4 w-4" />
              Create group
            </button>
            <button
              type="button"
              onClick={() => loadBaseData({ preserveLoading: true })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </header>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5" />
              <div>
                <p className="font-semibold">Action could not be completed</p>
                <p className="mt-1 text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!selectedGroupId ? (
          <>
            <section className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Groups</p>
                <p className="mt-2 text-3xl font-black text-slate-950">{groups.length}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Memberships</p>
                <p className="mt-2 text-3xl font-black text-slate-950">{totalMembers}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Available permissions</p>
                <p className="mt-2 text-3xl font-black text-slate-950">{permissions.length}</p>
              </div>
            </section>

            <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-bold text-slate-950">Organization access groups</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Select a group to review it. Nothing is editable from this overview.
                  </p>
                </div>
                <label className="relative block w-full sm:max-w-xs">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="search"
                    aria-label="Search groups"
                    value={groupSearch}
                    onChange={(event) => setGroupSearch(event.target.value)}
                    placeholder="Search groups"
                    className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  />
                </label>
              </div>

              <div className="p-4">
                {filteredGroups.length === 0 ? (
                  <EmptyState
                    title="No groups found"
                    description="Create a custom group or adjust your search."
                  />
                ) : (
                  <div className="grid gap-3 lg:grid-cols-2">
                    {filteredGroups.map((group) => (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => openGroup(group.id)}
                        className="group rounded-2xl border border-slate-200 bg-white p-5 text-left transition hover:border-orange-200 hover:bg-orange-50/40 hover:shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="truncate font-bold text-slate-950">{group.name}</h3>
                              {group.is_system_group && (
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                                  System
                                </span>
                              )}
                            </div>
                            <p className="mt-2 line-clamp-2 text-sm text-slate-500">
                              {group.description || 'No description provided.'}
                            </p>
                          </div>
                          <ChevronRight className="h-5 w-5 shrink-0 text-slate-400 transition group-hover:text-orange-600" />
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-600">
                          <span className="rounded-full bg-slate-50 px-2.5 py-1 ring-1 ring-slate-200">
                            {group.members_count || 0} members
                          </span>
                          <span className="rounded-full bg-slate-50 px-2.5 py-1 ring-1 ring-slate-200">
                            {group.permissions_count || 0} permissions
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </>
        ) : detailLoading || !selectedGroup ? (
          <div className="flex min-h-[420px] items-center justify-center rounded-3xl border border-slate-200 bg-white">
            <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
          </div>
        ) : (
          <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-6">
              <button
                type="button"
                onClick={closeGroup}
                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-900"
              >
                <ArrowLeft className="h-4 w-4" />
                All groups
              </button>
              <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-2xl font-bold text-slate-950">{selectedGroup.name}</h2>
                    {selectedGroup.is_system_group && (
                      <span className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700 ring-1 ring-orange-100">
                        System group
                      </span>
                    )}
                    {!selectedGroup.is_active && (
                      <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 ring-1 ring-red-100">
                        Inactive
                      </span>
                    )}
                  </div>
                  <p className="mt-2 max-w-3xl text-sm text-slate-500">
                    {selectedGroup.description || 'No description provided.'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <span className="rounded-xl bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                    {(selectedGroup.members || []).length} members
                  </span>
                  <span className="rounded-xl bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                    {(selectedGroup.permissions || []).length} permissions
                  </span>
                </div>
              </div>
            </div>

            <nav className="flex gap-1 overflow-x-auto border-b border-slate-200 bg-slate-50/70 px-4 py-2" aria-label="Group management sections">
              {Object.entries(tabLabels).map(([tab, label]) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    if (permissionEditing && tab !== 'permissions' && hasPermissionChanges) {
                      if (!window.confirm('Discard unsaved permission changes?')) return;
                      cancelPermissionEdit();
                    }
                    setActiveTab(tab);
                  }}
                  className={cx(
                    'whitespace-nowrap rounded-xl px-4 py-2 text-sm font-semibold transition',
                    activeTab === tab
                      ? 'bg-white text-orange-700 shadow-sm ring-1 ring-slate-200'
                      : 'text-slate-600 hover:bg-white hover:text-slate-900',
                  )}
                >
                  {label}
                </button>
              ))}
            </nav>

            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="grid gap-5 lg:grid-cols-3">
                  <button
                    type="button"
                    onClick={() => setActiveTab('members')}
                    className="rounded-2xl border border-slate-200 p-5 text-left transition hover:border-orange-200 hover:bg-orange-50/40"
                  >
                    <Users className="h-5 w-5 text-orange-600" />
                    <h3 className="mt-4 font-bold text-slate-950">Manage members</h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Review current users or add Microsoft-synced identities.
                    </p>
                    <p className="mt-4 text-2xl font-black text-slate-950">
                      {(selectedGroup.members || []).length}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('permissions')}
                    className="rounded-2xl border border-slate-200 p-5 text-left transition hover:border-orange-200 hover:bg-orange-50/40"
                  >
                    <KeyRound className="h-5 w-5 text-orange-600" />
                    <h3 className="mt-4 font-bold text-slate-950">Review permissions</h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Permissions remain read-only until edit mode is explicitly enabled.
                    </p>
                    <p className="mt-4 text-2xl font-black text-slate-950">
                      {(selectedGroup.permissions || []).length}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('policy')}
                    className="rounded-2xl border border-slate-200 p-5 text-left transition hover:border-orange-200 hover:bg-orange-50/40"
                  >
                    <ShieldCheck className="h-5 w-5 text-orange-600" />
                    <h3 className="mt-4 font-bold text-slate-950">View policy scope</h3>
                    <p className="mt-1 text-sm text-slate-500">
                      Inspect the effective policy metadata attached to this group.
                    </p>
                  </button>
                </div>
              )}

              {activeTab === 'members' && (
                <div className="space-y-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="font-bold text-slate-950">Group members</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Membership controls effective local RBAC authorization.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openAddMembers}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700"
                    >
                      <UserPlus className="h-4 w-4" />
                      Add members
                    </button>
                  </div>

                  <label className="relative block max-w-sm">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="search"
                      aria-label="Search current members"
                      value={memberListSearch}
                      onChange={(event) => setMemberListSearch(event.target.value)}
                      placeholder="Search current members"
                      className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                    />
                  </label>

                  {filteredMembers.length === 0 ? (
                    <EmptyState
                      title="No members found"
                      description="Add a local or Microsoft-synced user to this group."
                      action={
                        <button
                          type="button"
                          onClick={openAddMembers}
                          className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700"
                        >
                          Add members
                        </button>
                      }
                    />
                  ) : (
                    <div className="overflow-hidden rounded-2xl border border-slate-200">
                      {filteredMembers.map((member) => (
                        <div
                          key={member.membership_id}
                          className="flex items-center justify-between gap-4 border-b border-slate-200 p-4 last:border-b-0"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-950">
                              {member.full_name || member.email}
                            </p>
                            <p className="truncate text-xs text-slate-500">{member.email}</p>
                          </div>
                          {!selectedGroup.is_system_group && (
                            <button
                              type="button"
                              onClick={() => removeMember(member.user_id)}
                              className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'permissions' && (
                <div className="space-y-5">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <h3 className="font-bold text-slate-950">Group permissions</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        {permissionEditing
                          ? 'Edit mode is active. Review all changes before applying them.'
                          : 'Read-only view. Use Edit permissions to make deliberate changes.'}
                      </p>
                    </div>
                    {!permissionEditing ? (
                      <button
                        type="button"
                        onClick={startPermissionEdit}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit permissions
                      </button>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={cancelPermissionEdit}
                          className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={!hasPermissionChanges}
                          onClick={() => setShowPermissionReview(true)}
                          className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Check className="h-4 w-4" />
                          Review changes
                          {hasPermissionChanges &&
                            ` (${permissionChanges.added.length + permissionChanges.removed.length})`}
                        </button>
                      </div>
                    )}
                  </div>

                  {permissionEditing && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                      Permission changes can immediately expand or reduce access. Changes are not saved until you review and confirm them.
                    </div>
                  )}

                  {Object.keys(permissionsByCategory).length === 0 ? (
                    <EmptyState
                      title="No permissions assigned"
                      description={
                        permissionEditing
                          ? 'Select permissions below to build this group’s access.'
                          : 'This group currently grants no permissions.'
                      }
                    />
                  ) : (
                    <div className="space-y-4">
                      {Object.entries(permissionsByCategory).map(([category, items]) => (
                        <div key={category} className="rounded-2xl border border-slate-200">
                          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                            <h4 className="text-sm font-bold text-slate-800">
                              {categoryLabels[category] || category}
                            </h4>
                          </div>
                          <div className="grid gap-2 p-3 xl:grid-cols-2">
                            {items.map((permission) => {
                              const checked = permissionDraftCodes.includes(permission.code);
                              return (
                                <label
                                  key={permission.code}
                                  className={cx(
                                    'flex items-start gap-3 rounded-xl border p-3',
                                    permissionEditing
                                      ? 'cursor-pointer border-slate-200 hover:bg-slate-50'
                                      : 'cursor-default border-transparent bg-slate-50/70',
                                    checked && permissionEditing && 'border-orange-200 bg-orange-50',
                                  )}
                                >
                                  {permissionEditing ? (
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() => togglePermission(permission.code)}
                                      className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                                    />
                                  ) : (
                                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <p className="text-sm font-semibold text-slate-950">{permission.name}</p>
                                      <RiskBadge risk={permission.risk_level} />
                                    </div>
                                    <p className="mt-1 text-xs text-slate-500">{permission.description}</p>
                                    <p className="mt-2 font-mono text-[11px] text-slate-400">{permission.code}</p>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'policy' && (
                <div>
                  <h3 className="font-bold text-slate-950">Policy scope</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Read-only representation of the policy attached to this group.
                  </p>
                  <pre className="mt-5 max-h-[520px] overflow-auto rounded-2xl bg-slate-950 p-5 text-xs leading-6 text-slate-100">
                    {JSON.stringify(selectedGroup.policy_json || {}, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </section>
        )}

        {showAddMembers && selectedGroup && (
          <Modal
            title={`Add members to ${selectedGroup.name}`}
            description="Search local and Microsoft-synced users. Assigning an eligible Microsoft user enables SSO and local RBAC together."
            onClose={() => !addingMembers && setShowAddMembers(false)}
            width="max-w-3xl"
            footer={
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-500">
                  {Object.keys(selectedCandidates).length} selected
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowAddMembers(false)}
                    disabled={addingMembers}
                    className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={addSelectedMembers}
                    disabled={addingMembers || Object.keys(selectedCandidates).length === 0}
                    className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {addingMembers ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                    Assign selected
                  </button>
                </div>
              </div>
            }
          >
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                aria-label="Search users"
                value={memberSearch}
                onChange={(event) => setMemberSearch(event.target.value)}
                placeholder="Search by name, email, UPN, or department"
                className="w-full rounded-xl border border-slate-300 py-3 pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
            </label>

            <div className="mt-4 space-y-2">
              {memberCandidatesLoading ? (
                <div className="flex items-center justify-center gap-2 rounded-2xl bg-slate-50 p-8 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
                  Searching users...
                </div>
              ) : memberCandidates.length === 0 ? (
                <EmptyState
                  title="No available users"
                  description="No local or Microsoft-synced users match this search."
                />
              ) : (
                memberCandidates.map((candidate) => {
                  const selected = Boolean(selectedCandidates[candidate.candidate_key]);
                  const isMicrosoft = candidate.source === 'microsoft_directory';
                  return (
                    <label
                      key={candidate.candidate_key}
                      className={cx(
                        'flex items-start gap-3 rounded-2xl border p-4 transition',
                        candidate.assignable ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
                        selected
                          ? 'border-orange-300 bg-orange-50'
                          : 'border-slate-200 bg-white hover:bg-slate-50',
                      )}
                    >
                      <input
                        type="checkbox"
                        aria-label={candidate.full_name || candidate.email}
                        checked={selected}
                        disabled={!candidate.assignable}
                        onChange={() => toggleCandidate(candidate)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-slate-950">
                            {candidate.full_name || candidate.email}
                          </p>
                          <span
                            className={cx(
                              'rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1',
                              isMicrosoft
                                ? 'bg-blue-50 text-blue-700 ring-blue-100'
                                : 'bg-slate-100 text-slate-600 ring-slate-200',
                            )}
                          >
                            {isMicrosoft ? 'Microsoft synced' : 'Local user'}
                          </span>
                          {candidate.local_account_state === 'pending_activation' && (
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
                              Pending SSO activation
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">{candidate.email}</p>
                        {isMicrosoft && candidate.assignable && !candidate.ready_for_login && (
                          <p className="mt-2 text-xs font-medium text-emerald-700">
                            {candidate.local_account_state === 'pending_activation'
                              ? 'Assignment will activate the existing onboarding account for Microsoft SSO and preserve its role.'
                              : 'Assignment will create or link a non-admin local identity and enable Microsoft SSO.'}
                          </p>
                        )}
                        {!candidate.assignable && (
                          <p className="mt-2 text-xs font-medium text-red-600">
                            {candidate.blocking_reason === 'local_account_disabled'
                              ? 'This local account was activated previously and later disabled by an administrator.'
                              : 'This Microsoft directory user is not currently eligible for access.'}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </Modal>
        )}

        {showPermissionReview && selectedGroup && (
          <Modal
            title="Review permission changes"
            description={`Confirm the exact changes for ${selectedGroup.name}.`}
            onClose={() => !savingPermissions && setShowPermissionReview(false)}
            footer={
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowPermissionReview(false)}
                  disabled={savingPermissions}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Continue editing
                </button>
                <button
                  type="button"
                  onClick={savePermissions}
                  disabled={savingPermissions || !hasPermissionChanges}
                  className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {savingPermissions ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Apply permission changes
                </button>
              </div>
            }
          >
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              This action changes effective authorization for every active member of the group and is audit logged.
            </div>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <h3 className="font-bold text-emerald-900">Added ({permissionChanges.added.length})</h3>
                <div className="mt-3 space-y-2">
                  {permissionChanges.added.length === 0 ? (
                    <p className="text-sm text-emerald-800/70">No permissions added.</p>
                  ) : (
                    permissionChanges.added.map((code) => (
                      <div key={code} className="rounded-xl bg-white p-3 ring-1 ring-emerald-100">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-900">
                            {permissionByCode[code]?.name || code}
                          </p>
                          <RiskBadge risk={permissionByCode[code]?.risk_level} />
                        </div>
                        <p className="mt-1 font-mono text-[11px] text-slate-500">{code}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                <h3 className="font-bold text-red-900">Removed ({permissionChanges.removed.length})</h3>
                <div className="mt-3 space-y-2">
                  {permissionChanges.removed.length === 0 ? (
                    <p className="text-sm text-red-800/70">No permissions removed.</p>
                  ) : (
                    permissionChanges.removed.map((code) => (
                      <div key={code} className="rounded-xl bg-white p-3 ring-1 ring-red-100">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-900">
                            {permissionByCode[code]?.name || code}
                          </p>
                          <RiskBadge risk={permissionByCode[code]?.risk_level} />
                        </div>
                        <p className="mt-1 font-mono text-[11px] text-slate-500">{code}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </Modal>
        )}

        {showCreateGroup && (
          <Modal
            title="Create custom group"
            description="Create a group with no permissions, then review members and access separately."
            onClose={() => !creatingGroup && setShowCreateGroup(false)}
            footer={
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateGroup(false)}
                  disabled={creatingGroup}
                  className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={createCustomGroup}
                  disabled={creatingGroup}
                  className="inline-flex items-center gap-2 rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
                >
                  {creatingGroup ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Create group
                </button>
              </div>
            }
          >
            <div className="space-y-4">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Group name</span>
                <input
                  value={newGroup.name}
                  onChange={(event) => setNewGroup((current) => ({ ...current, name: event.target.value }))}
                  placeholder="Example: Payroll Reviewer"
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-slate-700">Description</span>
                <textarea
                  value={newGroup.description}
                  onChange={(event) => setNewGroup((current) => ({ ...current, description: event.target.value }))}
                  rows={4}
                  placeholder="Describe who should use this group and why."
                  className="w-full resize-none rounded-xl border border-slate-300 px-4 py-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                />
              </label>
              <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4 text-sm text-orange-900">
                New groups start with no permissions and metadata-only policy. This avoids accidental privilege grants during creation.
              </div>
            </div>
          </Modal>
        )}
      </div>
    </PermissionGate>
  );
}
